import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { getApiBaseUrl } from '../lib/utils';
import NotificationToggles from './NotificationToggles';
import FeedbackModal from './FeedbackModal';

function formatUnitDetail(value) {
  if (value === undefined || value === null || value === '') return 'Not specified';
  if (Array.isArray(value)) return value.length === 0 ? 'Not specified' : value.join(', ');
  return value;
}

function AccountPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, getToken, refreshUser, logout } = useAuth();

  const [email, setEmail] = useState('');

  const [notifications, setNotifications] = useState({
    newBulletin: true,
    newForumPost: false,
    newTicket: false,
    newForumComment: true,
    forumMention: true
  });
  const [hideUnitFromForum, setHideUnitFromForum] = useState(false);
  const [noticeConsent, setNoticeConsent] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = useState('');
  const [deleteAccountLoading, setDeleteAccountLoading] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setEmail(user.email || '');
      if (user.notificationPreferences) {
        setNotifications({
          newBulletin: user.notificationPreferences.newBulletin ?? true,
          newForumPost: user.notificationPreferences.newForumPost ?? false,
          newTicket: user.notificationPreferences.newTicket ?? false,
          newForumComment: user.notificationPreferences.newForumComment ?? true,
          forumMention: user.notificationPreferences.forumMention ?? true
        });
      }
      setHideUnitFromForum(user.hideUnitFromForum ?? false);
      setNoticeConsent(!!user.noticeConsent);
    }
  }, [user]);

  // Strata notices by email need the owner's consent for one address (Strata Property Act s.61).
  const selfManaged = (user?.condos || []).some((c) => c && c.selfManaged);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);


    setSaving(true);

    try {
      const payload = {
        notificationPreferences: notifications,
        hideUnitFromForum
      };
      if (selfManaged && noticeConsent !== !!user.noticeConsent) {
        payload.noticeConsent = noticeConsent;
      }

      if (email !== user.email) {
        payload.email = email;
      }

      await axios.put(
        `${getApiBaseUrl()}/api/auth/account`,
        payload,
        {
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );

      setSuccess('Account updated successfully');
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteAccountLoading(true);
    setDeleteAccountError(null);
    try {
      await axios.delete(
        `${getApiBaseUrl()}/api/auth/account`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      logout();
      navigate('/login');
    } catch (err) {
      setDeleteAccountError(err.response?.data?.error || 'Failed to delete account');
    } finally {
      setDeleteAccountLoading(false);
    }
  };

  const closeDeleteAccountModal = () => {
    setShowDeleteAccountModal(false);
    setDeleteAccountConfirmText('');
    setDeleteAccountError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const unitInfo = user.unitInfo;
  const unitNumber = unitInfo?.number;
  const hasUnit = unitNumber !== undefined && unitNumber !== null && unitNumber !== '';

  return (
    <div className="min-h-screen bg-background py-8 px-4 pb-20 md:pb-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-card rounded-lg shadow-xl border border-border p-8">
          <h1 className="text-3xl font-bold text-foreground mb-6">Account Settings</h1>

          <form onSubmit={handleSubmit} className="space-y-8">
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border">
                Profile Information
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={user.name}
                    disabled
                    className="w-full px-4 py-3 bg-muted border border-border rounded-lg text-muted-foreground cursor-not-allowed"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Role
                    </label>
                    <input
                      type="text"
                      value={user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : ''}
                      disabled
                      className="w-full px-4 py-3 bg-muted border border-border rounded-lg text-muted-foreground cursor-not-allowed capitalize"
                    />
                  </div>
                  {hasUnit && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Unit Number
                      </label>
                      <input
                        type="text"
                        value={unitNumber}
                        disabled
                        className="w-full px-4 py-3 bg-muted border border-border rounded-lg text-muted-foreground cursor-not-allowed"
                      />
                    </div>
                  )}
                </div>

                {unitInfo && (
                  <div className="rounded-lg border border-border bg-muted/50 p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3">Unit Details</h3>
                    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Parking</dt>
                        <dd className="mt-1 text-sm text-foreground">{formatUnitDetail(unitInfo.parking)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Locker</dt>
                        <dd className="mt-1 text-sm text-foreground">{formatUnitDetail(unitInfo.locker)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Bicycle Storage</dt>
                        <dd className="mt-1 text-sm text-foreground">{formatUnitDetail(unitInfo.bicycle)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Type</dt>
                        <dd className="mt-1 text-sm text-foreground capitalize">{formatUnitDetail(unitInfo.type)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sqft</dt>
                        <dd className="mt-1 text-sm text-foreground">{formatUnitDetail(unitInfo.sqft)}</dd>
                      </div>
                      {unitInfo.features && unitInfo.features.length > 0 && (
                        <div>
                          <dt className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Features</dt>
                          <dd className="mt-1 text-sm text-foreground">{unitInfo.features.join(', ')}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Unit details are read-only. If something looks incorrect, contact an administrator through the{' '}
                  <button
                    type="button"
                    onClick={() => setShowFeedbackModal(true)}
                    className="text-primary hover:underline"
                  >
                    admin contact page
                  </button>
                  .
                </p>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
                    required
                    disabled={saving}
                  />
                </div>
              </div>
            </section>

            {hasUnit && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border">
                  Privacy
                </h2>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hideUnitFromForum}
                    onChange={(e) => setHideUnitFromForum(e.target.checked)}
                    disabled={saving}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Hide my unit number from forum posts and comments</span>
                </label>
              </section>
            )}

            {selfManaged && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border">
                  Strata notices
                </h2>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={noticeConsent}
                    onChange={(e) => setNoticeConsent(e.target.checked)}
                    disabled={saving}
                    className="w-4 h-4 mt-1 rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">
                    Send official strata notices (like AGM packages) to {user.noticeConsent?.email || user.email} instead of by mail
                    {user.noticeConsent && (
                      <span className="block text-xs text-muted-foreground">Agreed {new Date(user.noticeConsent.consentedAt).toLocaleDateString()}. Changing your email address removes this agreement.</span>
                    )}
                  </span>
                </label>
              </section>
            )}

            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4 pb-2 border-b border-border">
                Email Notifications
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Choose which email notifications you'd like to receive
              </p>
              <NotificationToggles
                preferences={notifications}
                onChange={setNotifications}
                disabled={saving}
              />
            </section>

            {error && (
              <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded">
                {success}
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground py-3 rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>

          <section className="mt-8 pt-8 border-t border-border">
            <h2 className="text-lg font-semibold text-foreground mb-4">Feedback</h2>
            <div className="border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">Provide feedback, ask a question, or report a bug</p>
                <p className="text-xs text-muted-foreground">Your message goes directly to the site administrator.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowFeedbackModal(true)}
                className="px-4 py-2 border border-border rounded-lg text-sm font-semibold text-foreground hover:bg-muted transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                Send Feedback
              </button>
            </div>
          </section>

          <section className="mt-8 pt-8 border-t border-destructive/30">
            <h2 className="text-lg font-semibold text-destructive mb-4">Danger Zone</h2>
            <div className="border border-destructive/30 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-foreground">Delete account</p>
                <p className="text-xs text-muted-foreground">Permanently delete your account and all associated data. This cannot be undone.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteAccountModal(true)}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-semibold hover:bg-destructive/90 transition-colors whitespace-nowrap w-full sm:w-auto"
              >
                Delete Account
              </button>
            </div>
          </section>
        </div>

        {showFeedbackModal && (
          <FeedbackModal onClose={() => setShowFeedbackModal(false)} />
        )}

        {showDeleteAccountModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl border border-border w-full max-w-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-destructive">Delete Account</h3>
                <button
                  onClick={closeDeleteAccountModal}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={deleteAccountLoading}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                This will <strong className="text-destructive">permanently delete your account and all your data</strong>. This action cannot be undone.
              </p>

              <p className="text-sm text-foreground mb-2">
                Type <strong>DELETE</strong> to confirm:
              </p>
              <input
                type="text"
                value={deleteAccountConfirmText}
                onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:ring-2 focus:ring-destructive focus:border-transparent text-foreground mb-4"
                disabled={deleteAccountLoading}
                data-testid="delete-account-confirm-input"
              />

              {deleteAccountError && (
                <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-3 py-2 rounded text-sm mb-4">
                  {deleteAccountError}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={closeDeleteAccountModal}
                  className="flex-1 px-4 py-3 border border-border text-foreground rounded-lg hover:bg-muted transition-colors"
                  disabled={deleteAccountLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountConfirmText !== 'DELETE' || deleteAccountLoading}
                  className="flex-1 bg-destructive text-destructive-foreground px-4 py-3 rounded-lg font-semibold hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="confirm-delete-account"
                >
                  {deleteAccountLoading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountPage;
